import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import './Admin.css';

const PAGE_SIZE = 100;

const ACTION_LABELS = {
  create_user:            'User Created',
  deactivate_user:        'User Deactivated',
  reactivate_user:        'User Reactivated',
  view_active_users:      'Viewed Users',
  view_deleted_users:     'Viewed Deleted Users',
  view_audit_logs:        'Viewed Audit Logs',
  login:                  'Login',
  login_failure:          'Login Failed',
  user_registration:      'Registration',
  email_verification:     'Email Verified',
  mfa_verification:       'MFA Verified',
  mfa_setup_initiated:    'MFA Setup',
  mfa_enabled:            'MFA Enabled',
  mfa_disabled:           'MFA Disabled',
  password_change:        'Password Changed',
  profile_update:         'Profile Updated',
  view_patients:          'Viewed Patients',
  view_patient:           'Viewed Patient',
  create_patient:         'Patient Created',
  update_patient:         'Patient Updated',
  delete_patient:         'Patient Deleted',
  view_claims:            'Viewed Claims',
  view_claim:             'Viewed Claim',
  create_claim:           'Claim Created',
  update_claim:           'Claim Updated',
  delete_claim:           'Claim Deleted',
  view_revenue_stats:     'Viewed Revenue',
  view_facilities:        'Viewed Facilities',
  create_facility:        'Facility Created',
  update_facility:        'Facility Updated',
  update_facility_status: 'Facility Status Changed',
  view_worklist:          'Viewed Worklist',
  view_claim_detail:      'Viewed Claim Detail',
  generate_claim:         'Claim Generated',
  emr_encounter_sync:     'EMR Sync',
  submit_claims:          'Claims Submitted',
  validate_claims:        'Claims Validated',
  generate_edi:           'EDI Generated',
  view_generated_claims:  'Viewed Generated Claims',
  view_edi_batches:       'Viewed EDI Batches',
  check_trizetto_status:  'Trizetto Status Check',
  resend_email_verification: 'Verification Resent',
};

const OUTCOME_COLORS = {
  success: { bg: 'rgba(5, 153, 141, 0.08)', color: '#05998d' },
  failure: { bg: '#fef2f2', color: '#dc2626' },
};

function getPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, '…', total];
  if (current >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '…', current - 1, current, current + 1, '…', total];
}

function AuditLogs() {
  const [logs, setLogs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);

  // Debounce search input — resets to page 1 when query changes
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setDebouncedSearch(value);
    }, 350);
  }, []);

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        // A search term is often a patient name or MRN, so it travels in the
        // request BODY — never the URL, where every request log would keep it.
        const data = debouncedSearch
          ? await api.post('/api/admin/audit-logs/search', { page, search: debouncedSearch })
          : await api.get(`/api/admin/audit-logs?${new URLSearchParams({ page, limit: PAGE_SIZE })}`);
        setLogs(data.logs);
        setTotal(data.total);
        setTotalPages(data.totalPages);
      } catch (err) {
        console.error('Failed to fetch audit logs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [page, debouncedSearch]);

  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd   = Math.min(page * PAGE_SIZE, total);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderDetails = (log) => {
    if (!log.details && !log.failureReason && !log.targetUsername) return '—';
    const d = log.details || {};

    if (log.failureReason) {
      return <span style={{ color: '#dc2626' }}>{log.failureReason.replace(/_/g, ' ')}</span>;
    }

    switch (log.action) {
      case 'create_user':
        return <span>Created <code>{d.newUserEmail}</code> as {d.newUserRole}</span>;
      case 'deactivate_user':
        return <span>Deactivated <code>{log.targetUsername}</code></span>;
      case 'reactivate_user':
        return <span>Reactivated <code>{log.targetUsername}</code></span>;
      case 'login':
        return <span>{d.mfaRequired ? 'MFA required' : d.method === 'trusted_device' ? 'Via trusted device' : '—'}</span>;
      case 'update_facility_status':
        return <span>{d.fromStatus} → {d.toStatus}</span>;
      case 'generate_claim':
        return <span>{d.claimNumber}{d.diagnosisCount != null ? ` · ${d.diagnosisCount} dx · ${d.serviceLineCount} svc` : ''}</span>;
      case 'submit_claims':
        return <span>{d.claimCount} claim{d.claimCount !== 1 ? 's' : ''}{d.batches?.length ? ` · ${d.batches.length} batch` : ''}</span>;
      case 'emr_encounter_sync':
        return <span>{d.action === 'created' ? 'Created' : 'Updated'} · {d.encounterId}</span>;
      case 'view_revenue_stats':
        return <span>{d.claimCount?.toLocaleString()} claims</span>;
      default:
        if (d.recordCount != null) return <span>{d.recordCount.toLocaleString()} records</span>;
        if (d.claimNumber)        return <span><code>{d.claimNumber}</code></span>;
        if (log.targetUsername)   return <span><code>{log.targetUsername}</code></span>;
        return '—';
    }
  };

  if (loading && logs.length === 0) {
    return (
      <div className="admin-page">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  const pageNumbers = getPageNumbers(page, totalPages);

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-header-text">
          <h1>Audit Logs</h1>
          <p>Track system activity and account changes for HIPAA compliance</p>
        </div>
      </div>

      <div className="admin-toolbar">
        <div className="admin-search-wrapper">
          <svg className="admin-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="admin-search"
            placeholder="Search by action, user, outcome, or details..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchQuery && (
            <button className="admin-search-clear" onClick={() => handleSearchChange('')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        <span className="audit-range-label">
          {loading
            ? 'Loading…'
            : total === 0
              ? 'No entries'
              : `Showing ${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()} of ${total.toLocaleString()}`}
        </span>
      </div>

      <div className="admin-table-card">
        {logs.length === 0 && !loading ? (
          <div className="admin-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            <h3>No audit logs found</h3>
            <p>{searchQuery ? 'Try adjusting your search' : 'System activity will be logged here'}</p>
          </div>
        ) : (
          <>
            <table className="admin-table" style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.15s' }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Performed By</th>
                  <th>Action</th>
                  <th>Outcome</th>
                  <th>Details</th>
                  <th>IP Address</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id}>
                    <td className="audit-date-cell">{formatDate(log.createdAt)}</td>
                    <td className="admin-name-cell">
                      {log.actor?.firstName ? (
                        <>
                          <div className="admin-avatar">{log.actor.firstName[0]}{log.actor.lastName?.[0]}</div>
                          <span>{log.actor.firstName} {log.actor.lastName}</span>
                        </>
                      ) : log.actor?.username ? (
                        <>
                          <div className="admin-avatar" style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', color: '#15803d' }}>
                            {log.actor.username[0].toUpperCase()}
                          </div>
                          <span style={{ color: '#6b7280' }}>{log.actor.username}</span>
                        </>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>System</span>
                      )}
                    </td>
                    <td>
                      <span className={`audit-action-badge ${log.action}`}>
                        {ACTION_LABELS[log.action] || log.action.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>
                      <span className="audit-outcome-badge" style={{
                        background: OUTCOME_COLORS[log.outcome]?.bg || '#f3f4f6',
                        color: OUTCOME_COLORS[log.outcome]?.color || '#6b7280',
                      }}>
                        {log.outcome}
                      </span>
                    </td>
                    <td className="audit-details">{renderDetails(log)}</td>
                    <td style={{ fontSize: '0.8rem', color: '#9ca3af', fontFamily: 'monospace' }}>
                      {log.ipAddress || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="audit-pagination">
                <button
                  className="audit-page-btn nav"
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 1 || loading}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                  Prev
                </button>

                <div className="audit-page-numbers">
                  {pageNumbers.map((n, i) =>
                    n === '…' ? (
                      <span key={`ellipsis-${i}`} className="audit-page-ellipsis">…</span>
                    ) : (
                      <button
                        key={n}
                        className={`audit-page-btn${n === page ? ' active' : ''}`}
                        onClick={() => setPage(n)}
                        disabled={loading}
                      >
                        {n}
                      </button>
                    )
                  )}
                </div>

                <button
                  className="audit-page-btn nav"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page === totalPages || loading}
                >
                  Next
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AuditLogs;
